/**
 * Точка входа общего бандла.
 *
 * Модули импортируются явно, без index-файлов: видно состав бандла
 * и tree-shaking работает предсказуемо.
 *
 * Тяжёлые библиотеки (gsap, swiper, intl-tel-input) в основной бандл
 * НЕ входят — компоненты подгружают их через await import() и только
 * при наличии своей разметки. Соответствие «компонент → библиотека»
 * описано в шапке каждого файла.
 */

// CSS библиотек подключается здесь, а не в SCSS: Sass не разрешает
// пути к пакетам. Идёт перед стилями темы, чтобы наши правила
// переопределяли их без !important.
//
// ⚠️ Остаётся статическим даже при динамическом JS: чанк подключается
// после main.css, и порядок переопределения перевернулся бы.
// Вес — 27 КБ на двоих; раздельный CSS по страницам — задача Этапа 5
import 'swiper/css';

// 🔴 CSS модулей Swiper — ОТДЕЛЬНЫМИ точками входа, `swiper/css` даёт
// только ядро (обёртка, слайды, трансформации).
//
// Понадобилось на Этапе 6 §0, когда со страниц блога был снят вендорный
// `swiper-bundle.min.css`: в нём модули встроены, а в npm-пакете нет.
// Сборка не падает, слайдер работает, слайды листаются — но стрелки
// теряют позиционирование и размеры. Визуальный регресс показал
// стабильный diff 1 % на `blog-list` и +5px высоты на `author-single`,
// и это НЕ выглядело как «пропал CSS»: правила `_swiper.scss` частично
// перекрывали нехватку, поэтому кнопки оставались на месте, но съезжали.
//
// ⚠️ Диагноз дался не сразу: сначала проверялись инициализация слайдеров
// (все 6 живы, модули подключены) и фаза автопрокрутки (`activeIndex: 0`
// в момент кадра — то есть ни при чём). Решающим был замер
// `grep swiper-button-next`: 15 вхождений в вендорном CSS против 0
// в собранном.
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/effect-fade';

import 'intl-tel-input/styles';

import '@scss/main.scss';

// Фиксы окружения: слушатели ставятся при импорте
import '@js/fixes/vh-fix';
import '@js/utils/scroll-detection';

import Sliders from '@js/components/sliders';
import BlogSliders from '@js/components/blog-sliders';

// Код одной конкретной страницы — в `pages/`, а не в `components/`.
// Сам модуль проверяет свою разметку и без неё ничего не грузит,
// поэтому попадание в общий бандл стоит десятки байт
import Silavslove from '@js/pages/silavslove';
import FrontPage from '@js/pages/front-page';
import SocialProjects from '@js/pages/social-projects';
import CourseAccordions from '@js/components/course-accordions';
import BlogDetail from '@js/pages/blog-detail';
import PageDocuments from '@js/pages/documents';
import Header from '@js/components/header';
import TopBar from '@js/components/topbar';
import NotFound from '@js/pages/not-found';
import Review from '@js/components/review-video';
import FormAnimation from '@js/components/form-animation';
import IntlTel from '@js/components/intl-tel';
import Form from '@js/components/form';
import FeedbackForm from '@js/components/feedback-form';
import ConsultationForm from '@js/components/consultation-form';
import CorporatePayment from '@js/components/corporate-payment';
import Accordion from '@js/components/accordion';

window.addEventListener('DOMContentLoaded', () => {
    // Асинхронные компоненты не ожидаются: синхронные не должны ждать
    // загрузки чужих чанков. Ошибку загрузки логируем, остальное работает
    const lazy = [
        Sliders,
        BlogSliders,
        Silavslove,
        FrontPage,
        SocialProjects,
        FormAnimation,
        IntlTel,
        // Статья блога: оглавление и липкий сайдбар синхронны, слайдер
        // «Новые статьи» догружает Swiper. В `lazy` ради этого await
        BlogDetail,
        PageDocuments,
        // Блок «Что-то непонятно?»: догружает легаси `Modals` чанком
        ConsultationForm,
    ];
    for (const init of lazy) {
        Promise.resolve(init()).catch((error) => {
            console.error(`${init.name}: не удалось загрузить библиотеку`, error);
        });
    }

    Header();
    TopBar();
    NotFound();
    Review();
    Form();

    // ⚠️ В инлайн-версии запуск был на `load`, здесь — `DOMContentLoaded`,
    // то есть РАНЬШЕ. Для этой формы безопасно: код страны из
    // intl-tel-input читается не при инициализации, а в момент submit
    // (см. `getDialCode` в модуле), поэтому гонки с динамическим
    // чанком библиотеки нет. Замер подтвердил: состав запроса
    // совпал с эталоном «до» поле за полем
    FeedbackForm();

    // Форма «Оплата от юрлица»: 6 шаблонов, раньше три копии в легаси
    CorporatePayment();

    // Синхронный, в `lazy` не идёт: своих библиотек не грузит,
    // только навешивает слушатели на аккордеоны шаблонов курсов
    CourseAccordions();

    // Единый аккордеон (бэклог §0.22). Ищет `[data-accordion]`,
    // без него выходит сразу. Заменит четыре прежние реализации —
    // пока работает параллельно с `CourseAccordions`, классы
    // у них разные, пересечься не могут
    Accordion();
});
